// Потеря соединения с IndexedDB: распознать и вернуть вкладке живую локальную БД.
//
// ── Механизм (Hawk 6ac895f91ae9690c18edf1ec, OXEM-676) ──
//
// Локальная SQLite PowerSync на вебе лежит в IndexedDB (VFS `IDBBatchAtomicVFS`
// из wa-sqlite). Само хранилище обслуживает не страница, а отдельный процесс
// браузера, и iOS может завершить его, пока Safari в фоне. Вернувшись, WebKit
// узнаёт, что связь с ним потеряна, и закрывает РАЗОМ все соединения IndexedDB
// — и документа, и его воркеров (`IDBConnectionProxy::connectionToServerLost`
// → `IDBDatabase::connectionToServerLost`: `m_closePending = true`, события
// `error` и `close`). С этого момента любой `transaction()` бросает
// `InvalidStateError: … The database connection is closing.`
//
// Соединения сами не оживают: открыть их заново может только тот, кто открыл, а
// VFS этого не умеет — у его `IDBContext` нет обработчика `close`, и после
// первого отказа очередь запросов отравлена до `reset()`, который так и не
// вызывается. Значит, локальная БД вкладки мертва до перезагрузки: запросы
// PowerSync падают, синк не пишет, офлайн-правки не сохраняются. Событие в Hawk
// пришло после 14 минут простоя, в момент возврата из фона: вкладка была жива,
// это не шум закрытия.
//
// ── Почему перезагрузка ──
//
// Та же логика, что у мёртвого чанка (`lib/chunk-load.ts`): в пределах
// документа соединение не оживает, оживает новый документ. Пересоздать БД на
// месте нельзя: `getPowerSync()` — синглтон, на нём держатся коннектор,
// слушатели статуса и императивные записи по всему приложению.
//
// ── Как узнаём о потере ──
//
// Способов два, потому что режимов БД два (`db-mode.ts`):
//   • часовой — своё крошечное соединение с отдельной базой. Потеря связи
//     закрывает соединения всех контекстов сразу, поэтому `close` часового
//     приходит и тогда, когда сама БД живёт в воркере. Отказы VFS из воркера до
//     окна не доходят, и без часового worker-режим ломался бы молча;
//   • отказ — `unhandledrejection` с той же ошибкой. Часть записей VFS запускает
//     без `await`, и в main-thread-режиме их отказ всплывает в окно. Ровно это и
//     было событием в Hawk;
//   • проба часового при возврате вкладки (OXEM-678) — `close` при потере связи
//     WebKit доставляет не всегда (WebKit #273827: «the close event does not
//     fire»), а iOS обрывает хранилище как раз пока вкладка в фоне. Без пробы
//     worker-режим в таком случае снова ломался бы молча.
//
// Слушатель отказов ставится в `main.tsx` ДО катчера Hawk — первым в очереди
// слушателей окна. Так он успевает остановить сырое событие: о потере Hawk
// узнаёт одним структурным событием отсюда, а не россыпью отказов отравленной
// очереди VFS.

import { toast } from "@studyhub/ui/toast";
import i18n from "@/i18n";
import { isAppVisible, onAppVisibilityChange } from "@/lib/app-visible";
import { getLastKnownOnline, subscribeOnlineStatus } from "@/lib/network-status";
import { captureClientError } from "@/lib/observability/hawk-client";
import { IS_CAPACITOR } from "@/lib/platform-detect";
import type { PowerSyncDbMode } from "./db-mode.js";

/** Отдельная база часового: в чужую (wa-sqlite) соваться нельзя — её схемой владеет VFS. */
const SENTINEL_DB_NAME = "studyhub-idb-sentinel";

const RELOAD_MARK_KEY = "studyhub:idb-loss-reload-at";

/**
 * Окно, внутри которого автоматическая перезагрузка не повторяется. Если
 * хранилище сломано по-настоящему и отказывает сразу после старта, страница
 * иначе перезагружалась бы по кругу.
 */
const RELOAD_COOLDOWN_MS = 60_000;

/** Пауза перед перезагрузкой — шанс событию Hawk уйти в коллектор. */
const RELOAD_DELAY_MS = 300;

const PROMPT_TOAST_ID = "idb-connection-lost";

export type IdbLossTrigger = "sentinel-close" | "sentinel-probe" | "rejection";

/**
 * Отказ из-за потерянного соединения или транзакции, а не дефект запроса.
 * Тексты движков: WebKit и Chromium — «The database connection is closing»,
 * WebKit — «Connection to Indexed Database server lost» и «without an
 * in-progress transaction», Firefox — «Can't start a transaction on a closed
 * database». Обычный «transaction aborted» принимается только со stack VFS.
 */
export function isIdbConnectionLossError(reason: unknown): boolean {
	if (typeof reason !== "object" || reason === null) return false;
	const { name, message, stack } = reason as {
		name?: unknown;
		message?: unknown;
		stack?: unknown;
	};
	if (typeof name !== "string" || typeof message !== "string") return false;
	const msg = message.toLowerCase();
	if (name === "InvalidStateError") {
		return (
			msg.includes("database connection is closing") ||
			msg.includes("start a transaction on a closed database")
		);
	}
	if (name === "UnknownError") {
		return (
			msg.includes("indexed database server lost") ||
			msg.includes("attempt to get a record from database without an in-progress transaction")
		);
	}
	return (
		name === "Error" &&
		msg === "transaction aborted" &&
		typeof stack === "string" &&
		stack.includes("IDBBatchAtomicVFS")
	);
}

export interface IdbLossRecoveryState {
	/** Вкладка скрыта: человека нет, свежего ответа о сети тоже. */
	hidden: boolean;
	online: boolean;
	/** Идёт работа, которую перезагрузка уничтожит (запись лекции). */
	busy: boolean;
	/** С прошлой автоматической перезагрузки прошло окно остывания. */
	cooledDown: boolean;
}

/**
 * - `wait-visible` — ничего не делать, пока вкладку не откроют;
 * - `wait-online` — предложить перезагрузку и сделать её самим, когда вернётся
 *   сеть: офлайн новый документ может упереться в чанки, которых нет в кэше;
 * - `prompt` — только предложить: решает человек;
 * - `reload` — перезагрузить самим.
 */
export type IdbLossRecoveryAction = "wait-visible" | "wait-online" | "prompt" | "reload";

/** Что делать после потери соединения. Чистая функция — проверяется без DOM. */
export function resolveIdbLossRecovery(state: IdbLossRecoveryState): IdbLossRecoveryAction {
	if (state.hidden) return "wait-visible";
	if (!state.online) return "wait-online";
	if (state.busy || !state.cooledDown) return "prompt";
	return "reload";
}

export interface IdbConnectionLossOptions {
	/**
	 * Идёт работа, которую перезагрузка уничтожит (запись лекции в полёте).
	 * Приходит параметром: `lib/` — лист графа и в сторы приложения не ходит.
	 * Голосового репетитора тут нет — его экран после перезагрузки сам
	 * продолжает сессию (`voiceTutor.sessions.resume`).
	 */
	isBusy: () => boolean;
}

let armedMode: PowerSyncDbMode | null = null;
let sentinel: IDBDatabase | null = null;
let lost = false;
let listenerInstalled = false;
let isBusy: () => boolean = () => false;

/**
 * Поставить слушатель отказов. Вызывать в `main.tsx` ДО `initHawkClient()`:
 * слушатели одного события вызываются в порядке регистрации, и только первый
 * может остановить сырое событие до катчера.
 *
 * Пока локальная БД не открыта (`armIdbConnectionLossGuard`), слушатель ничего
 * не делает: у гостя чужой отказ IndexedDB уходит в Hawk как обычно.
 */
export function installIdbConnectionLossListener(options: IdbConnectionLossOptions): void {
	// В приложении БД нативная (`@powersync/capacitor`), IndexedDB ей не нужен.
	if (IS_CAPACITOR || listenerInstalled || typeof window === "undefined") return;
	listenerInstalled = true;
	isBusy = options.isBusy;
	window.addEventListener("unhandledrejection", (event) => {
		if (armedMode === null || !isIdbConnectionLossError(event.reason)) return;
		// Отказ разобран здесь и уйдёт в Hawk одним структурным событием, а не
		// по разу на каждый отказ отравленной очереди VFS.
		event.preventDefault();
		event.stopImmediatePropagation();
		handleConnectionLoss("rejection", event.reason);
	});
}

/**
 * Локальная БД открыта — следить за соединением. Вызывает фабрика БД
 * (`db-impl.web.ts`), то есть ровно тогда, когда БД появилась на вкладке.
 */
export function armIdbConnectionLossGuard(mode: PowerSyncDbMode): void {
	if (armedMode !== null) return;
	armedMode = mode;
	openSentinel();
	onAppVisibilityChange(probeSentinelOnResume);
}

/**
 * Соединение часового закрывается браузером? Проба синхронная и ничего не
 * стоит: хранилищ у часового нет, поэтому `transaction([])` на живом
 * соединении бросает InvalidAccessError (пустой список), а на закрывающемся —
 * InvalidStateError «connection is closing»: флаг закрытия спецификация
 * проверяет раньше списка хранилищ (IndexedDB 3.0, `IDBDatabase.transaction()`,
 * шаги 2 и 5). Любой другой исход — не потеря: перезагружать по догадке нельзя.
 */
export function isSentinelConnectionLost(db: Pick<IDBDatabase, "transaction">): boolean {
	try {
		db.transaction([]);
		return false;
	} catch (error) {
		return isIdbConnectionLossError(error);
	}
}

function probeSentinelOnResume(): void {
	if (lost || !sentinel || !isAppVisible()) return;
	if (isSentinelConnectionLost(sentinel)) handleConnectionLoss("sentinel-probe");
}

function openSentinel(): void {
	if (typeof indexedDB === "undefined") return;
	let request: IDBOpenDBRequest;
	try {
		request = indexedDB.open(SENTINEL_DB_NAME, 1);
	} catch {
		return;
	}
	request.onsuccess = () => {
		const db = request.result;
		// `close` приходит ТОЛЬКО при аварийном закрытии: своё `db.close()` и
		// выгрузка документа его не порождают — ложной тревоги на уходе нет.
		db.onclose = () => handleConnectionLoss("sentinel-close");
		// Базу часового хотят удалить или обновить — уступаем: это не авария.
		// Ссылку снимаем, иначе проба на возврате приняла бы своё же закрытие
		// за потерю.
		db.onversionchange = () => {
			sentinel = null;
			db.close();
		};
		sentinel = db;
	};
	// Не открылась (приватный режим, запрет хранилища) — живём без часового:
	// в main-thread-режиме потерю всё равно поймает слушатель отказов.
	request.onerror = (event) => event.preventDefault();
}

function handleConnectionLoss(trigger: IdbLossTrigger, reason?: unknown): void {
	// Одна потеря — одна реакция: следом за первым отказом приходят `close`
	// часового и отказы остальных записей VFS.
	if (lost) return;
	lost = true;
	const action = resolveIdbLossRecovery(readRecoveryState());
	const { name, message } = (reason ?? {}) as { name?: unknown; message?: unknown };
	captureClientError(new Error("powersync: соединение с IndexedDB потеряно"), {
		scope: "powersync.idb-connection-lost",
		trigger,
		mode: armedMode ?? "unknown",
		action,
		error_name: typeof name === "string" ? name : undefined,
		error_message: typeof message === "string" ? message : undefined,
	});
	recover(action);
}

function readRecoveryState(): IdbLossRecoveryState {
	return {
		hidden: !isAppVisible(),
		// Последняя проба общего монитора. «Ещё не знаем» бывает только до первой
		// пробы (монитор держит `ReconnectRecoveryProvider` всё время работы
		// приложения) — тогда считаем, что сеть есть: `navigator.onLine` здесь
		// не советчик, он залипает в обе стороны.
		online: getLastKnownOnline() !== false,
		busy: isBusy(),
		cooledDown: Date.now() - lastAutoReloadAt() > RELOAD_COOLDOWN_MS,
	};
}

function recover(
	action: IdbLossRecoveryAction = resolveIdbLossRecovery(readRecoveryState()),
): void {
	switch (action) {
		case "wait-visible": {
			const unsubscribe = onAppVisibilityChange(() => {
				if (!isAppVisible()) return;
				unsubscribe();
				recover();
			});
			return;
		}
		case "wait-online": {
			showReloadPrompt();
			let unsubscribe: (() => void) | null = null;
			let settled = false;
			unsubscribe = subscribeOnlineStatus((online) => {
				if (!online || settled) return;
				settled = true;
				// Подписка отдаёт известное значение синхронно — до того, как
				// `unsubscribe` присвоен; тогда снимаемся на следующем тике.
				queueMicrotask(() => unsubscribe?.());
				recover();
			});
			return;
		}
		case "prompt":
			showReloadPrompt();
			return;
		case "reload":
			// Без записанной метки окно остывания не работает, и сломанное
			// хранилище перезагружало бы страницу по кругу. Решает человек.
			if (!markAutoReload()) {
				showReloadPrompt();
				return;
			}
			window.setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
	}
}

function showReloadPrompt(): void {
	toast(i18n.t("common:sync.localDbLost.message"), {
		id: PROMPT_TOAST_ID,
		duration: Number.POSITIVE_INFINITY,
		action: {
			label: i18n.t("common:sync.localDbLost.action"),
			// Ручное нажатие проходит всегда; метка — чтобы автоматика нового
			// документа не перезагрузила его повторно сразу после старта.
			onClick: () => {
				markAutoReload();
				window.location.reload();
			},
		},
	});
}

function lastAutoReloadAt(): number {
	try {
		return Number(window.sessionStorage.getItem(RELOAD_MARK_KEY)) || 0;
	} catch {
		return 0;
	}
}

function markAutoReload(): boolean {
	try {
		window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
		return true;
	} catch {
		return false;
	}
}
